/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `dive_alarm` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class DiveAlarmMesg : Mesg {
    public constructor() : super("DiveAlarm", Profile.MesgNum.DIVE_ALARM)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. Index of the alarm */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** depth, in m. Depth setting (m) for depth type alarms */
    public var depth: Double?
        get() = getFieldValue(DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(DEPTH_FIELD_NUM, value)

    /** time, in s. Time setting (s) for time type alarms */
    public var time: Int?
        get() = getFieldValue(TIME_FIELD_NUM) as? Int
        set(value) = setFieldValue(TIME_FIELD_NUM, value)

    /** enabled. Enablement flag */
    public var enabled: Boolean?
        get() = (getFieldValue(ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** alarm_type. Alarm type setting */
    public var alarmType: DiveAlarmType?
        get() = (getFieldValue(ALARM_TYPE_FIELD_NUM) as? UByte)?.let { DiveAlarmType.fromValue(it) }
        set(value) = setFieldValue(ALARM_TYPE_FIELD_NUM, value?.value)

    /** sound. Tone and Vibe setting for the alarm */
    public var sound: Tone?
        get() = (getFieldValue(SOUND_FIELD_NUM) as? UByte)?.let { Tone.fromValue(it) }
        set(value) = setFieldValue(SOUND_FIELD_NUM, value?.value)

    /** Number of values stored in dive_types. */
    public val numDiveTypes: Int
        get() = getField(DIVE_TYPES_FIELD_NUM)?.numValues ?: 0

    /** dive_types. Dive types the alarm will trigger on */
    public var diveTypes: List<SubSport?>?
        get() {
            val field = getField(DIVE_TYPES_FIELD_NUM) ?: return null
            return List(field.numValues) { getDiveTypes(it) }
        }
        set(value) {
            removeField(DIVE_TYPES_FIELD_NUM)
            value?.forEachIndexed { index, element -> setDiveTypes(index, element) }
        }

    public fun getDiveTypes(index: Int): SubSport? =
        (getFieldValue(DIVE_TYPES_FIELD_NUM, index) as? UByte)?.let { SubSport.fromValue(it) }

    public fun setDiveTypes(index: Int, value: SubSport?) {
        setFieldValue(DIVE_TYPES_FIELD_NUM, value?.value, index)
    }

    /** id. Alarm ID */
    public var id: UInt?
        get() = getFieldValue(ID_FIELD_NUM) as? UInt
        set(value) = setFieldValue(ID_FIELD_NUM, value)

    /** popup_enabled. Show a visible pop-up for this alarm */
    public var popupEnabled: Boolean?
        get() = (getFieldValue(POPUP_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(POPUP_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** trigger_on_descent. Trigger the alarm on descent */
    public var triggerOnDescent: Boolean?
        get() = (getFieldValue(TRIGGER_ON_DESCENT_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(TRIGGER_ON_DESCENT_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** trigger_on_ascent. Trigger the alarm on ascent */
    public var triggerOnAscent: Boolean?
        get() = (getFieldValue(TRIGGER_ON_ASCENT_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(TRIGGER_ON_ASCENT_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** repeating. Repeat alarm each time threshold is crossed? */
    public var repeating: Boolean?
        get() = (getFieldValue(REPEATING_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(REPEATING_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** speed, in mps. Ascent/descent rate (mps) setting for speed type alarms */
    public var speed: Double?
        get() = getFieldValue(SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(SPEED_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val DEPTH_FIELD_NUM: UByte = 0u
        public const val TIME_FIELD_NUM: UByte = 1u
        public const val ENABLED_FIELD_NUM: UByte = 2u
        public const val ALARM_TYPE_FIELD_NUM: UByte = 3u
        public const val SOUND_FIELD_NUM: UByte = 4u
        public const val DIVE_TYPES_FIELD_NUM: UByte = 5u
        public const val ID_FIELD_NUM: UByte = 6u
        public const val POPUP_ENABLED_FIELD_NUM: UByte = 7u
        public const val TRIGGER_ON_DESCENT_FIELD_NUM: UByte = 8u
        public const val TRIGGER_ON_ASCENT_FIELD_NUM: UByte = 9u
        public const val REPEATING_FIELD_NUM: UByte = 10u
        public const val SPEED_FIELD_NUM: UByte = 11u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[0] = Field("Depth", 0u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[1] = Field("Time", 1u, BaseType.SINT32, 1.0, 0.0, "s", false)
            fields[2] = Field("Enabled", 2u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[3] = Field("AlarmType", 3u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[4] = Field("Sound", 4u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[5] = Field("DiveTypes", 5u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[6] = Field("Id", 6u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[7] = Field("PopupEnabled", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("TriggerOnDescent", 8u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[9] = Field("TriggerOnAscent", 9u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[10] = Field("Repeating", 10u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[11] = Field("Speed", 11u, BaseType.SINT32, 1000.0, 0.0, "mps", false)
            return fields
        }
    }
}
